import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { notify } from "@/components/ui/notify";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import {
	CategoryMappingEditor,
	foldPhrase,
	phraseAnchorId,
} from "@/features/catalog/components/category-mapping-editor";
import {
	commitImportRun,
	discardImportRun,
	getImportRun,
	type ImportAction,
	type ImportMatchStatus,
	type ImportRow,
	type ImportRun,
	type RowDecision,
	resolveImportRun,
	revertImportRun,
	reviewImportRow,
	type UnpricedGroup,
} from "@/features/catalog/services/import-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { hasPermission } from "@/lib/permissions";
import { BASIS_OPTIONS } from "../components/source-dialog.js";
import { FIELD_TARGETS } from "../lib/import-template.js";

/**
 * One import run: what it would do, what a person has to decide, and the two
 * buttons that make it real.
 *
 * FIVE SURFACES ON ONE ROUTE, and the plan flags that as under-specified --
 * plan 010 spent seven units on the bulk actions of a single dashboard. What is
 * here is the flow end to end so that M3 (a non-engineer completes an import
 * unaided) is runnable; splitting it per surface is the next round's work, and
 * the plan says so rather than this comment claiming it is finished.
 *
 * The column mapping lives HERE rather than on the upload form because it needs
 * the file's headers, which nothing knows until the file has been read.
 */

const IGNORE = "__ignore";

/**
 * The targets offered for ONE column.
 *
 * A `spec:` column can only be itself. The server routes any target beginning
 * `spec:` into the product's specifications, but `FIELD_TARGETS` is pinned to
 * the server's nine writable FIELDS and a specification is not one of them --
 * so before this, a column called `spec:Material` had no selectable target at
 * all. It stayed unmapped, and on a category whose template requires that
 * attribute every imported row picked up a publish blocker for a value the file
 * was carrying all along.
 *
 * Offered rather than forced: "ignore" is still available, and a `spec:` column
 * can still be pointed at an ordinary field if the header was a mistake.
 */
function targetsFor(header: string): Array<{ value: string; label: string }> {
	if (!header.startsWith("spec:")) return FIELD_TARGETS;
	return [
		...FIELD_TARGETS,
		{
			value: header,
			label: `Specification: ${header.slice("spec:".length)}`,
		},
	];
}

/**
 * The field a column would map to if nothing else had already decided.
 *
 * MIRRORS `identityMapping` in `import.service.ts`, including the parts that
 * look like accidents: trimmed for the comparison and raw for the key, and a
 * bare `spec:` is not a specification. It is mirrored rather than imported
 * because the admin cannot reach backend code. It decides whether the banner
 * renders and what the offer contains -- the server still does the mapping.
 */
function identityTargetFor(header: string): string | null {
	const name = header.trim();
	if (name.startsWith("spec:")) {
		return name.slice("spec:".length).trim() === "" ? null : name;
	}
	return FIELD_TARGETS.some((one) => one.value !== "" && one.value === name)
		? name
		: null;
}

/**
 * What this run's mapping will and will not write, before anything is written.
 *
 * `fieldNamedButIgnored` STATES A FACT rather than inferring a cause, and it took
 * a review round to get there. The first version claimed the source's saved
 * mapping was in use, reasoning that a field-named unmapped column could only
 * have come from the carried-forward branch of `stageImport`'s precedence chain.
 * It cannot: `setImportMapping` DROPS empty targets, so an operator who
 * deliberately sets such a column to "ignore" and presses the dry run stores
 * exactly this state -- and the banner would have told them a saved mapping was
 * in use, which is false, while offering a button that undid their own choice.
 *
 * The carried-forward mapping is still the usual cause and still worth naming as
 * one. Saying WHICH it was needs the server to record the branch it took, the way
 * `hold_reason` records a row's; until then the copy names both possibilities.
 *
 * ONE CONDITION, NOT TWO. The plan asked for the banner whenever the saved
 * mapping is in use and the offer only when it would change a header. Those are
 * one condition: the banner is about the columns the offer would change, so it
 * has nothing to say when there are none.
 */
function mappingState(
	header: string[],
	effective: Record<string, string>,
): {
	unmapped: string[];
	noPrice: boolean;
	fieldNamedButIgnored: boolean;
	offer: Record<string, string>;
} {
	/*
	 * FOLDED, because `normalizeRow` folds: it looks each header up in
	 * `byLoweredHeader`, trimmed and lower-cased. An exact lookup here reported a
	 * column as unmapped when a mapping carried forward under `Mrp` matched a
	 * header spelled `MRP` -- so the banner said "will write nothing" about a
	 * column the server does write.
	 */
	const mapped = new Set(
		Object.keys(effective)
			.filter((column) => effective[column])
			.map((column) => column.trim().toLowerCase()),
	);
	const unmapped = header.filter(
		(column) => !mapped.has(column.trim().toLowerCase()),
	);
	/*
	 * `price` IS EXCLUDED, because a second thing explains it. A harvest passes
	 * `withholdFields: ["price"]`, so a harvested document whose header names
	 * `price` arrives with it deliberately unmapped -- and counting that here
	 * would put this banner on every first harvest. Nothing is lost: an unmapped
	 * `price` is what the third banner is about, and its picker is on screen
	 * either way.
	 */
	/*
	 * Column and target together, so the target is resolved once and the loop below
	 * needs no second null check. It had one, and it was dead: an empty header
	 * cannot reach here because `identityTargetFor` rejects it, so the two guards
	 * covered each other and either could be deleted with the suite green.
	 */
	const wouldChange: Array<[string, string]> = [];
	for (const column of unmapped) {
		const target = identityTargetFor(column);
		if (target !== null && target !== "price") {
			wouldChange.push([column, target]);
		}
	}

	const offer: Record<string, string> = { ...effective };
	for (const [column, target] of wouldChange) {
		offer[column] = target;
	}

	return {
		unmapped,
		noPrice: !Object.values(effective).includes("price"),
		fieldNamedButIgnored: wouldChange.length > 0,
		offer,
	};
}

/**
 * What the operator is confirming about the prices in this file.
 *
 * Its own function, following `bulk-confirm-dialog.tsx`'s `bulkIntentSentence`
 * convention, so the dialog has one source of wording. Exported so its two edge
 * branches can be asserted directly rather than through the dialog; unlike
 * `bulkIntentSentence`, nothing else renders it.
 *
 * Named in the source's own words, from `BASIS_OPTIONS` -- the list the dialog
 * that SET the basis uses. A second list here would let the run say "MRP" where
 * the source says something else, on the screen whose whole job is catching that.
 *
 * That list carries 7 of `priceBasisEnum`'s 14 values, being the 7 the source
 * dialog offers. A basis outside it -- reachable by seeding, not through the
 * admin -- prints its raw slug rather than nothing.
 */
export function basisSentence(basis: string | null): string {
	if (!basis) {
		/*
		 * UNREACHABLE in normal operation, and worth saying rather than implying.
		 * `catalog_sources.default_basis` is NOT NULL, `stageImport` is the only
		 * insert into `catalog_import_runs` and always copies it, and nothing updates
		 * `basis` afterwards -- so reaching this needs a direct database write.
		 *
		 * Kept because the column is nullable and the alternative is rendering
		 * "recorded as null" at an operator. "This run", not "this source", because
		 * the source's column is the one that cannot be null.
		 */
		return "This run records no price basis, so nothing states what these prices mean.";
	}
	const label =
		BASIS_OPTIONS.find((one) => one.value === basis)?.label ?? basis;
	return `Every price in this file will be recorded as ${label}.`;
}

/** How each action reads to somebody who did not write the pipeline. */
const ACTION_LABELS: Record<ImportAction, string> = {
	create: "New product",
	update: "Changed",
	price_only: "Price only",
	skip: "Unchanged",
};

/**
 * The source's own category words, when mapping them is what this row waits on.
 *
 * READS THE ROW'S REASON AS A VALUE. Until `hold_reason` existed this matched a
 * substring of the English sentence the planner wrote, so a copy edit on the
 * server would have removed the button from the rows it was the remedy for, and
 * a contract test was the only thing standing in the way. The sentence is still
 * what the operator reads; this is what the code branches on.
 *
 * Phrase presence cannot substitute for it. `resolveRowCategory` resolves by
 * SLUG first, so a row whose slug resolved and whose unit is missing carries
 * source words that were never consulted -- and a DECLINED phrase carries them
 * too, with the decision already made.
 */
function heldPhraseOf(row: ImportRow): string | null {
	if (row.holdReason !== "unmapped_phrase") return null;
	const words = (row.normalized as { sourceCategory?: unknown } | null)
		?.sourceCategory;
	return typeof words === "string" && words.length > 0 ? words : null;
}

/**
 * Put the operator on the picker for a phrase, not merely near it.
 *
 * Scrolling alone leaves a keyboard or screen-reader operator exactly where
 * they were while the page moves underneath them -- their next Tab continues
 * from the row they left, tens of phrases away from the control they asked for.
 */
function focusPhrase(sourceCategory: string): boolean {
	const anchor = document.getElementById(phraseAnchorId(sourceCategory));
	// Absent when the editor has not rendered this phrase -- its own fetch is
	// still in flight, or it failed. Silently doing nothing on a click reads as
	// a broken button, so the caller says so instead.
	if (!anchor) return false;
	anchor.scrollIntoView({ block: "center" });
	anchor.querySelector("input")?.focus();
	return true;
}

const GROUPS: Array<{
	key: string;
	label: string;
	matchStatus?: ImportMatchStatus;
	action?: ImportAction;
	/**
	 * Rows held back, which neither of the other two filters can express.
	 *
	 * A held-back row has no action but does have an error, and `matchStatus`
	 * and `action` are both equality predicates -- so before U11's server-side
	 * filter these rows matched no group at all and could not be opened. On one
	 * staging run that hid 100 of them behind a tile that counted them.
	 */
	hold?: "needs_mapping";
}> = [
	{ key: "queue", label: "Needs a decision", matchStatus: "ambiguous" },
	{ key: "hold", label: "Waiting on a category", hold: "needs_mapping" },
	{ key: "errors", label: "Could not be read", matchStatus: "error" },
	{ key: "create", label: "New products", action: "create" },
	{ key: "update", label: "Changed", action: "update" },
	{ key: "price", label: "Price only", action: "price_only" },
	{ key: "skip", label: "Unchanged", action: "skip" },
];

/**
 * A value as an operator would read it, not as the column stores it.
 *
 * `price` is the one that matters. It is stored in PAISE, so the raw value for
 * a Rs 1,250.00 list price is `125000` -- and a reviewer scanning a dry run
 * reads that as twelve and a half lakh. Rendered as rupees, with the field name
 * deciding, because nothing about the number itself says which it is.
 */
export function readable(value: unknown, field?: string): string {
	if (value === null || value === undefined) return "—";
	if (field === "price" && typeof value === "number") {
		return new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			minimumFractionDigits: 2,
		}).format(value / 100);
	}
	/*
	 * `packQty` is stored x1000, the scaling `catalog_products.pack_hierarchy`
	 * uses so a 0.5 kg sachet is an integer. Printed raw, a file that said 1
	 * read as 1000.
	 */
	if (field === "packQty" && typeof value === "number") {
		return new Intl.NumberFormat("en-IN", {
			maximumFractionDigits: 3,
		}).format(value / 1000);
	}
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
}

/**
 * A diff field's name as the column picker names it, so the dry run and the
 * mapping use the same words. A `specifications.<name>` key reads as that
 * attribute's name; any other key the picker does not list prints as stored.
 */
export function fieldLabel(field: string): string {
	const target = FIELD_TARGETS.find((one) => one.value === field);
	if (target) return target.label;
	if (field.startsWith("specifications.")) {
		return `Spec: ${field.slice("specifications.".length)}`;
	}
	return field;
}

function products(count: number): string {
	return `${count} product${count === 1 ? "" : "s"}`;
}

/**
 * Why a group of committed products shows customers no price, and what fixes
 * it, in one sentence.
 *
 * A remedy is named only where one exists. A GST rate is added on Catalogue >
 * GST rates (#514), and saving it re-prices the products whose HSN code falls
 * under its prefix. A product's HSN code is on its Basics
 * tab, but saving it does not re-project prices, so the price is recorded again.
 */
export function unpricedSentence(group: UnpricedGroup): string {
	const who = products(group.products);
	const date = group.rateEffectiveFrom
		? formatCalendarDate(group.rateEffectiveFrom)
		: null;
	switch (group.reason) {
		case "no_gst_rate":
			return `${who}: no GST rate for HSN ${group.hsnCode} is on record for the date of the price in force, so the price including GST cannot be worked out. The prices are stored; they are shown once a rate for HSN ${group.hsnCode} covering that date is added under Catalogue > GST rates (it needs the tax owner's sign-off), which re-prices them. No new import is needed.`;
		case "rate_not_yet_effective":
			return `${who}: GST rates for HSN ${group.hsnCode} are on record only from ${date}, and the price in force is dated earlier, so it is not shown.`;
		case "no_hsn_code":
			return `${who}: neither the product nor its category has an HSN code, so no GST rate can be looked up. Set the HSN code on the product's Basics tab, then record its price again on the Pricing tab, dated today.`;
		case "basis_not_shown":
			return `${who}: the price is on a basis that is stored for reference and never shown to customers. To show one, record a price on a basis that can be shown, on the product's Pricing tab.`;
		default: {
			// Fails to compile when the union gains a reason this switch lacks;
			// at run time, a reason the server sent first says what is certain.
			const unknown: never = group.reason;
			void unknown;
			return `${who}: the price is stored but not shown to customers.`;
		}
	}
}

/**
 * A run dated after its commit. Its prices are stored, the projection skips a
 * price not yet in effect, and the date arriving re-projects nothing: the
 * sweep does, run by hand, as does any later price write to a product. The
 * projection had nothing to classify, so whether each price is then shown
 * (basis, GST rate) is unknown here.
 */
export function notYetInEffectSentence(pending: {
	products: number;
	effectiveFrom: string;
}): string {
	const date = formatCalendarDate(pending.effectiveFrom);
	return `This run is dated ${date}, after it was committed, so the prices it recorded for ${products(pending.products)} are not in effect yet. The date arriving does not switch them on: running \`pnpm db:sweep:prices\` (engineering) on or after ${date} does, and so does any price change to a product made on or after that date (for that product). Until then, whatever each product showed before this run stays shown. This screen does not check a run dated ahead: whether each new price is then shown still depends on its basis, the product's HSN code and the GST rate on record for that date.`;
}

/**
 * How many of a commit's products did not reach the search index, or null when
 * all did, none were offered (0 of 0), or the server predates the fields.
 *
 * `none` is every product offered and none written: new products will not
 * appear in search and updated ones may show old data. `some` is a shortfall
 * short of that.
 */
export function searchShortfall(result: {
	productsOfferedToIndex?: number;
	productsIndexed?: number;
}): { offered: number; indexed: number; kind: "none" | "some" } | null {
	const { productsOfferedToIndex: offered, productsIndexed: indexed } = result;
	if (typeof offered !== "number" || typeof indexed !== "number") return null;
	if (indexed >= offered) return null;
	return { offered, indexed, kind: indexed <= 0 ? "none" : "some" };
}

/**
 * The body of the shortfall panel, agreeing in number with how many products
 * are in question: all of those offered when none reached search, otherwise
 * the ones the index left out.
 */
function shortfallBody(shortfall: {
	offered: number;
	indexed: number;
	kind: "none" | "some";
}): string {
	if (shortfall.kind === "none") {
		return shortfall.offered === 1
			? "The product is saved. If it is new, it will not appear in search; if it was updated, it may show old data, until the search index is rebuilt."
			: "The products are saved. New products will not appear in search, and updated products may show old data, until the search index is rebuilt.";
	}
	const left = shortfall.offered - shortfall.indexed;
	return left === 1
		? "The other 1 is saved. If it is new, it will not appear in search; if it was updated, it may show old data, until the search index is rebuilt."
		: `The other ${left} are saved. Any new products among them will not appear in search, and any updated ones may show old data, until the search index is rebuilt.`;
}

/** The command the "How to fix this" disclosure displays for the operator to run. */
export const REINDEX_COMMAND =
	"pnpm --filter backend search:reindex -- --products-only";

function DiffTable({ row }: { row: ImportRow }) {
	const entries = Object.entries(row.diff ?? {});
	if (entries.length === 0) {
		return (
			<p className="text-sm text-muted-foreground">Nothing would change.</p>
		);
	}
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-sm">
				<tbody>
					{entries.map(([field, change]) => (
						<tr key={field} className="border-b last:border-0">
							<td className="py-1 pr-4 font-medium">{fieldLabel(field)}</td>
							<td className="py-1 pr-4 text-muted-foreground line-through">
								{readable(change.from, field)}
							</td>
							<td className="py-1">{readable(change.to, field)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function CatalogImportRunPage() {
	const { runId = "" } = useParams();
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);
	const canCommit = hasPermission(operator, "admin:catalog:write");
	const canRevert = hasPermission(operator, "admin:catalog:publish");

	const commitTitleId = useId();
	const revertTitleId = useId();
	const discardTitleId = useId();
	const basisAckId = useId();
	const basisAckReasonId = useId();

	const [group, setGroup] = useState("queue");
	const [mapping, setMapping] = useState<Record<string, string> | null>(null);
	/*
	 * THE BANNER, AND NO TOAST -- the split `product-editor.tsx` settled on.
	 *
	 * `api-client.ts` toasts the server's message for every failure EXCEPT a 401,
	 * so a screen that toasts too shows two. But removing these handlers
	 * outright, which the previous commit did, left a 401 showing nothing at all
	 * on two of its three branches: no toast, no banner, no navigation. A session
	 * expiring mid-commit looked exactly like a click that did not register --
	 * on the one screen where the actions are destructive.
	 */
	const [actionError, setActionError] = useState<string | null>(null);
	const [confirming, setConfirming] = useState<
		"commit" | "revert" | "discard" | null
	>(null);
	/*
	 * The counts the dialog opened with, FROZEN.
	 *
	 * Plan 010 U5's lesson: a background refetch that moves the numbers under a
	 * confirmation turns "commit 412 rows" into a sentence the operator never
	 * agreed to. What they read is what they confirmed.
	 */
	const [frozen, setFrozen] = useState<ImportRun | null>(null);
	/*
	 * Reset on every OPEN, not on close. A tick that survives leaves the second
	 * commit of a session unguarded, and the operator never sees the sentence they
	 * are said to have agreed to.
	 */
	const [basisAcknowledged, setBasisAcknowledged] = useState(false);
	/*
	 * WHICH OF THE THREE SURFACES IS OPEN. They were three stacked cards, which
	 * put the category editor below a twenty-row column list and above a
	 * two-hundred-row queue -- with 109 phrases on a real Legrand run that is a
	 * scroll nobody finishes. The sketch this screen was designed from shows them
	 * as tabs; this is that.
	 *
	 * Rows by default: it is the surface an operator opens a run to read. The
	 * banners above say when the other two need attention, and the Categories tab
	 * carries its own count.
	 */
	const [surface, setSurface] = useState<"columns" | "categories" | "rows">(
		"rows",
	);
	/*
	 * A phrase asked for from the Rows tab, focused once Categories has rendered.
	 *
	 * "Map this phrase" sits on a held-back row and aims at a picker the other tab
	 * owns, so it cannot focus anything in the same tick -- the picker does not
	 * exist yet. Remembering it across the tab switch is what makes the link work.
	 */
	const [pendingPhrase, setPendingPhrase] = useState<string | null>(null);

	const selected = GROUPS.find((g) => g.key === group) ?? GROUPS[0];

	const detail = useQuery({
		queryKey: ["catalog-import-run", runId, group],
		queryFn: () =>
			getImportRun(runId, {
				matchStatus: selected.matchStatus,
				action: selected.action,
				hold: selected.hold,
				perPage: 100,
			}),
		enabled: Boolean(runId),
		placeholderData: (previous) => previous,
	});

	/*
	 * THE PHRASE COUNTS, ASKED FOR ONCE AND NOT PER GROUP.
	 *
	 * Its own key so switching tabs does not refetch it, and `perPage: 1`
	 * because only the aggregate is wanted -- the rows come from the query
	 * above. The counts are computed over the whole run by the server; deriving
	 * them from the 100 rows that query fetches would order the editor by a
	 * truncated number on any run bigger than a page.
	 */
	const phrases = useQuery({
		queryKey: ["catalog-import-run-phrases", runId],
		queryFn: () =>
			getImportRun(runId, {
				hold: "needs_mapping",
				includePhrases: true,
				perPage: 1,
			}),
		enabled: Boolean(runId),
	});

	const run = detail.data?.run;
	/*
	 * WHAT THE MAPPING CARD IS SHOWING, DECIDED ONCE.
	 *
	 * A row's "Map this phrase" moves focus to a picker this card owns, so the
	 * button reads this rather than deciding for itself. The round before gated it
	 * on the card being MOUNTED, which is also true while the card holds a spinner
	 * or an error and has rendered no picker at all.
	 *
	 * `ready` means THIS query settled. It does not mean a picker exists: the
	 * editor runs its own request for the source's saved mappings and renders
	 * nothing but an error if that fails, which is why `focusPhrase` still returns
	 * a boolean and the button still has something to say when it comes back
	 * false.
	 *
	 * `null` means no card at all: no source, or nothing held back.
	 */
	const heldPhrases = phrases.data?.heldBackPhrases;
	const mappingCard =
		!run?.sourceId || (run?.rowsNeedMapping ?? 0) === 0
			? null
			: phrases.isPending
				? ({ state: "loading" } as const)
				: phrases.isError
					? ({ state: "failed" } as const)
					: ({
							state: "ready",
							phrases: heldPhrases ?? [],
							/*
							 * Rows held back with NO phrase at all, which is a bucket of its
							 * own in the aggregate. Passed separately because an empty phrase
							 * list otherwise reads as "you are done" -- a price list with no
							 * category column holds every row this way.
							 *
							 * `find`, not `[0]`: the aggregate orders by count descending, so
							 * the no-phrase bucket is wherever its size puts it.
							 */
							unmappableRows:
								(heldPhrases ?? []).find((one) => !one.sourceCategory)?.rows ??
								0,
						} as const);

	/**
	 * Whether this row's phrase is among the ones the card was given.
	 *
	 * Not "has a picker on screen": see `mappingCard`. Folded on both sides,
	 * because the label beside a picker is one representative spelling the
	 * server's `min()` chose over the folded group, so a row spelling it
	 * differently would fail an exact comparison and lose its button.
	 *
	 * The two queries agree on any single settled snapshot: the aggregate is
	 * computed over the same predicate as the group, without a limit. What this
	 * adds is the windows where they are NOT both settled -- while the phrase
	 * query is in flight or has failed, and briefly after a save, because
	 * `invalidate` refetches both keys and only the row query keeps its previous
	 * data while refetching. In that window a row can be on screen with its
	 * phrase not yet back, and withholding the button is the right answer.
	 */
	const rowPhraseIsListed = (row: ImportRow): boolean => {
		if (mappingCard?.state !== "ready") return false;
		const words = heldPhraseOf(row);
		if (!words) return false;
		const key = foldPhrase(words);
		/*
		 * Empty after folding is not a phrase. The list carries a no-phrase bucket
		 * entry, and a key of "" would match it and hand the row a button aimed at
		 * an entry that renders no picker.
		 *
		 * UNREACHABLE TODAY, and no test covers it -- reverting this line leaves the
		 * whole suite green. It takes three files to see why: `heldPhraseOf` returns
		 * a phrase only when `hold_reason` is `unmapped_phrase`, and
		 * `resolveRowCategory` sets that reason only for a phrase that is non-empty
		 * after `.trim()`. Kept because that chain is the only thing holding the
		 * invariant, `sourceCategory` is nullable on both sides, and the failure it
		 * prevents is a button that silently does nothing.
		 */
		if (!key) return false;
		return mappingCard.phrases.some(
			(one) =>
				one.sourceCategory != null && foldPhrase(one.sourceCategory) === key,
		);
	};
	const header = run?.header ?? [];
	const effectiveMapping = mapping ?? run?.columnMapping ?? {};
	const columns = mappingState(header, effectiveMapping);

	useEffect(() => {
		if (surface !== "categories" || !pendingPhrase) return;
		const found = focusPhrase(pendingPhrase);
		// Cleared either way, so a phrase that cannot be found is not retried on
		// every later render.
		setPendingPhrase(null);
		if (!found) {
			notify.partly(
				"Could not jump to that phrase. The category list below says why.",
			);
		}
	}, [surface, pendingPhrase]);

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ["catalog-import-run", runId] });
		// Its own key, so it needs its own line. The counts the editor orders by
		// stop being true the moment a mapping lands, and a phrase left showing
		// its old count reads as a save that did not take.
		queryClient.invalidateQueries({
			queryKey: ["catalog-import-run-phrases", runId],
		});
		queryClient.invalidateQueries({ queryKey: ["catalog-import-runs"] });
	};

	const resolve = useMutation({
		/*
		 * The mapping goes WITH the dry run, when the operator changed one. The
		 * route writes it and re-reads the rows in one transaction, so a failure
		 * part-way cannot leave the run carrying a mapping its rows were never
		 * read with. Without sending it at all, this editor would be the thing
		 * CLAUDE.md warns about: a control that appears to do something the app
		 * never performs.
		 */
		/*
		 * A VARIABLE, NOT THE CLOSURE. Saving a category also re-runs the dry run,
		 * and reading `mapping` here would make that re-run carry the operator's
		 * unconfirmed column draft -- writing a column mapping they never pressed
		 * a button to send.
		 */
		mutationFn: (columnMapping?: Record<string, string>) =>
			resolveImportRun(runId, columnMapping),
		onSuccess: (result, columnMapping) => {
			invalidate();
			// Discharged only when this run actually sent it. A dry run triggered by
			// a saved mapping sends none, and clearing the draft there would discard
			// column edits the operator still has open.
			if (columnMapping) setMapping(null);
			notify.done(
				`${result.rowsNew} new, ${result.rowsChanged} changed, ${result.rowsUnmatched} to review, ${result.rowsNeedMapping} needing a category mapping`,
			);
		},
		onError: (error) =>
			setActionError(apiErrorMessage(error, "Could not run the dry run.")),
	});

	const review = useMutation({
		// Cleared on every attempt, not only on the three buttons that raise it:
		// the banner sits in the summary card, a different card from the queue
		// rows, so a stale "could not record that decision" would otherwise hang
		// over the next decision that worked.
		onMutate: () => setActionError(null),
		mutationFn: ({
			rowId,
			decision,
		}: {
			rowId: string;
			decision: RowDecision;
		}) => reviewImportRow(runId, rowId, decision),
		onSuccess: () => {
			invalidate();
			notify.done("Decision recorded");
		},
		onError: (error) =>
			setActionError(apiErrorMessage(error, "Could not record that decision.")),
	});

	const commit = useMutation({
		mutationFn: () => commitImportRun(runId),
		onSuccess: (result) => {
			invalidate();
			setConfirming(null);
			const shortfall = searchShortfall(result);
			/*
			 * Every condition that applies is named, so one cannot hide another. The
			 * search shortfall has this toast and its panel below; the stale
			 * projections have only this toast. The unpriced and not-yet-in-effect
			 * detail is in the panels below, which stay after the toast has gone.
			 */
			const notes: string[] = [];
			if (shortfall) {
				notes.push(
					shortfall.kind === "none"
						? `${
								shortfall.offered === 1
									? "It did not reach search yet."
									: "None of them reached search yet."
							} The panel on this page says what to do.`
						: `${shortfall.indexed} of ${shortfall.offered} reached search. The panel on this page says what to do.`,
				);
			}
			// Stale projections take priority over the unpriced and not-yet-in-effect
			// notes, so only one of the three is named. That is the order this toast
			// already had before the search note was added.
			if (result.pricesStale.length > 0) {
				// The commit landed and some price PROJECTIONS did not. Those
				// products still quote the price this run replaced.
				notes.push(
					result.pricesStale.length === 1
						? "1 product still shows the previous price; its projection could not be recomputed."
						: `${result.pricesStale.length} products still show the previous price; their projection could not be recomputed.`,
				);
			} else if (result.unpriced.products > 0) {
				notes.push(
					`${products(result.unpriced.products)} ${
						result.unpriced.products === 1 ? "has" : "have"
					} a price customers do not see. The panel on this page says why.`,
				);
			} else if (result.notYetInEffect) {
				notes.push(
					"This run's prices are not in effect yet. The panel on this page says when they are shown.",
				);
			}
			if (notes.length > 0) {
				notify.partly(
					`${result.productsCreated} created, ${result.productsUpdated} updated`,
					{ description: notes.join(" ") },
				);
				return;
			}
			notify.done(
				`${result.productsCreated} created, ${result.productsUpdated} updated`,
				{
					description:
						"Every new product is a draft until somebody publishes it.",
				},
			);
		},
		onError: (error) =>
			setActionError(apiErrorMessage(error, "Could not commit this run.")),
	});

	const revert = useMutation({
		mutationFn: () => revertImportRun(runId),
		onSuccess: (result) => {
			invalidate();
			setConfirming(null);
			if (result.conflicts.length > 0) {
				/*
				 * A conflict is not a failure and must not read as one. These are
				 * the products the revert deliberately did not touch because
				 * somebody else's work is on them.
				 */
				notify.partly(`Reverted, with ${result.conflicts.length} left alone`, {
					description: result.conflicts[0].reason,
				});
			} else {
				notify.done(
					`${result.productsDeleted} deleted, ${result.pricesCompensated} prices put back`,
				);
			}
		},
		onError: (error) =>
			setActionError(apiErrorMessage(error, "Could not revert this run.")),
	});

	const discard = useMutation({
		mutationFn: () => discardImportRun(runId),
		onSuccess: () => {
			invalidate();
			setConfirming(null);
			notify.done("Run discarded");
		},
		onError: (error) =>
			setActionError(apiErrorMessage(error, "Could not discard this run.")),
	});

	const revertConflicts = revert.data?.conflicts ?? [];
	/*
	 * From the commit response, so it lasts while this page is open. The same
	 * result is in the run's audit row, which this page does not read back.
	 */
	const unpriced = commit.data?.unpriced;
	const notYetInEffect = commit.data?.notYetInEffect ?? null;
	const shortfall = commit.data ? searchShortfall(commit.data) : null;

	if (!runId) return null;

	if (detail.isPending) {
		return (
			<div className="grid gap-3">
				<Skeleton className="h-24 w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (detail.isError || !run) {
		return (
			<div className="grid gap-3 rounded-2xl border border-dashed p-6 text-center">
				<p className="text-sm text-muted-foreground">
					Could not load this run.
				</p>
				<div>
					<Button variant="outline" size="sm" onClick={() => detail.refetch()}>
						Try again
					</Button>
				</div>
			</div>
		);
	}

	/*
	 * `resolving` and `committing` are states the enum has and nothing writes:
	 * both passes run synchronously inside their request. Read anyway, so that
	 * the day a long import moves to a background job this screen already
	 * refuses to act on a run that is mid-flight rather than needing to be
	 * remembered.
	 */
	const busy = run.status === "resolving" || run.status === "committing";
	const rows = detail.data?.rows ?? [];

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle className="flex items-center gap-2">
							{run.fileName ?? "(file removed)"}
							<StatusBadge status={run.status} />
						</CardTitle>
						<CardDescription>
							{run.sourceName ?? "(source removed)"} · {run.rowsTotal} rows ·
							prices read as {run.basis ?? "no basis"}
						</CardDescription>
					</div>
					<div className="flex gap-2">
						<Button variant="outline" size="sm" asChild>
							<a
								href={`/api/v1/admin/catalog/imports/${runId}/document`}
								download
							>
								Download file
							</a>
						</Button>
						<Button variant="outline" size="sm" asChild>
							<Link to="/catalog/imports">All runs</Link>
						</Button>
					</div>
				</CardHeader>
				<CardContent className="grid gap-4">
					<dl className="grid grid-cols-2 gap-3 md:grid-cols-6">
						{[
							["New", run.rowsNew],
							["Changed", run.rowsChanged],
							["Unchanged", run.rowsUnchanged],
							["To review", run.rowsUnmatched],
							// Its own tile: these rows are not unmatched, they are
							// unmapped, and without it the tiles no longer sum to the
							// row count.
							["Needs mapping", run.rowsNeedMapping],
							["Errored", run.rowsErrored],
						].map(([label, value]) => (
							<div key={String(label)} className="rounded-2xl border p-3">
								<dt className="text-xs text-muted-foreground">{label}</dt>
								<dd className="text-xl font-semibold tabular-nums">{value}</dd>
							</div>
						))}
					</dl>

					<div className="flex flex-wrap gap-2">
						<Button
							size="sm"
							variant="outline"
							disabled={
								resolve.isPending ||
								busy ||
								run.status === "committed" ||
								run.status === "reverted" ||
								run.status === "discarded"
							}
							onClick={() => {
								setActionError(null);
								resolve.mutate(mapping ?? undefined);
							}}
						>
							{resolve.isPending
								? "Working…"
								: mapping
									? "Save columns and run the dry run"
									: "Run the dry run"}
						</Button>
						{canCommit ? (
							<Button
								size="sm"
								disabled={run.status !== "resolved" || commit.isPending || busy}
								onClick={() => {
									setFrozen(run);
									setBasisAcknowledged(false);
									setConfirming("commit");
								}}
							>
								Commit
							</Button>
						) : null}
						{run.status === "staged" || run.status === "resolved" ? (
							<Button
								size="sm"
								variant="outline"
								disabled={discard.isPending || busy}
								onClick={() => setConfirming("discard")}
							>
								Discard
							</Button>
						) : null}
						{canRevert && run.status === "committed" ? (
							<Button
								size="sm"
								variant="destructive"
								disabled={revert.isPending}
								onClick={() => {
									setFrozen(run);
									setConfirming("revert");
								}}
							>
								Revert
							</Button>
						) : null}
					</div>

					{actionError ? (
						<p className="text-sm text-destructive" role="alert">
							{actionError}
						</p>
					) : null}

					{shortfall && run.status === "committed" ? (
						<div
							role={shortfall.kind === "none" ? "alert" : "status"}
							className="grid gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3"
						>
							<p
								className={
									shortfall.kind === "none"
										? "text-sm font-medium text-destructive"
										: "text-sm font-medium text-amber-900"
								}
							>
								{shortfall.kind === "none"
									? `${products(shortfall.offered)} committed, none reached search`
									: `${shortfall.indexed} of ${products(shortfall.offered)} committed reached search`}
							</p>
							<p className="text-sm text-amber-900">
								{shortfallBody(shortfall)} This alert is from this commit and
								will not reappear after the page is reloaded. Import run:{" "}
								{run.id}.
							</p>
							<details className="text-sm text-amber-900">
								<summary className="cursor-pointer font-medium">
									How to fix this
								</summary>
								<div className="mt-2 grid gap-2">
									<p>
										The admin cannot rebuild the index. Whoever runs the backend
										can, with:
									</p>
									<pre className="overflow-x-auto rounded bg-muted p-2 text-foreground">
										{REINDEX_COMMAND}
									</pre>
									<p>
										The command above is safe to run again. If it reports that
										the collection schema is out of date, add{" "}
										<code>--drop</code>: that empties the products collection in
										search until the rebuild finishes.
									</p>
								</div>
							</details>
						</div>
					) : null}

					{unpriced && unpriced.products > 0 && run.status === "committed" ? (
						<div
							role="status"
							className="grid gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3"
						>
							<p className="text-sm font-medium text-amber-900">
								{products(unpriced.products)} this run priced have a price
								customers do not see
							</p>
							<ul className="grid gap-1 text-sm text-amber-900">
								{unpriced.groups.map((group) => (
									<li
										key={`${group.reason}|${group.hsnCode ?? ""}|${group.rateEffectiveFrom ?? ""}`}
									>
										{unpricedSentence(group)}
									</li>
								))}
							</ul>
						</div>
					) : null}

					{notYetInEffect && run.status === "committed" ? (
						<p
							role="status"
							className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
						>
							{notYetInEffectSentence(notYetInEffect)}
						</p>
					) : null}

					{revertConflicts.length > 0 ? (
						<div className="grid gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
							<p className="text-sm font-medium">
								{revertConflicts.length} left alone
							</p>
							<ul className="grid gap-1 text-sm text-muted-foreground">
								{revertConflicts.map((conflict) => (
									<li key={conflict.productId}>
										<Link
											className="underline"
											to={`/catalog/products/${conflict.productId}`}
										>
											{conflict.name}
										</Link>{" "}
										— {conflict.reason}
									</li>
								))}
							</ul>
						</div>
					) : null}
				</CardContent>
			</Card>

			{/*
			 * WHAT WILL NOT BE WRITTEN, ABOVE THE PICKERS THAT FIX IT. Three separate
			 * statements rather than one summary, because they have different remedies
			 * and only one of them is a mistake.
			 *
			 * The amber panel is the house style for this screen (the revert-conflict
			 * panel below uses it); no new colour, which the `tailwind-tokens` ratchet
			 * would refuse anyway.
			 */}
			{header.length > 0 && columns.unmapped.length > 0 ? (
				<div className="grid gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
					<p className="text-sm font-medium text-amber-900">
						{columns.unmapped.length === 1
							? "One column will write nothing"
							: `${columns.unmapped.length} columns will write nothing`}
					</p>
					{/*
					 * NAMED, not counted. A count tells an operator that something is
					 * wrong without telling them which of twenty pickers to touch.
					 */}
					<p className="text-sm text-amber-900">
						{columns.unmapped.join(", ")} — kept with each row so a reviewer can
						still read them, but not written to any product.
					</p>
					{columns.fieldNamedButIgnored ? (
						<>
							<p className="text-sm text-amber-900">
								Some of those headers already name a field, and are set to
								ignore anyway. Usually that is this source's saved mapping being
								carried forward over the headers in this file; it is also what
								you would see if somebody set them to ignore on purpose.
							</p>
							{/*
							 * No second condition on the button: reaching this branch at all
							 * means a header would change -- see `mappingState`. A brand's own
							 * export names none of our fields, and on those runs neither this
							 * sentence nor the button appears.
							 */}
							<Button
								className="justify-self-start"
								disabled={
									run?.status !== "staged" && run?.status !== "resolved"
								}
								onClick={() => setMapping(columns.offer)}
								size="sm"
								type="button"
								variant="outline"
							>
								Use this file's headers instead
							</Button>
						</>
					) : null}
				</div>
			) : null}

			{/*
			 * INFORMATION, NOT A FAILURE, and deliberately not in the amber panel above.
			 * A product with no price is a legitimate record here -- prices differ by
			 * region and arrive in their own file -- so styling this as a problem would
			 * misreport a state ops reach on purpose.
			 */}
			{header.length > 0 && columns.noPrice ? (
				<p className="text-sm text-muted-foreground">
					No column maps to price, so these products will be committed with no
					price recorded. A later file can carry the prices on their own.
				</p>
			) : null}

			{/*
			 * One strip for the three surfaces below. Rendered whenever the run has
			 * a header, which is every run that was read at all.
			 */}
			{header.length > 0 ? (
				<div className="flex flex-wrap gap-2">
					{(
						[
							["columns", "Columns", header.length],
							[
								"categories",
								"Categories",
								/*
								 * ROWS waiting, not phrases. It is the same number as the tile
								 * above and the one that decides whether the run can finish;
								 * a phrase count would read as a size rather than as work.
								 * Null hides the badge on a run with nothing waiting.
								 */
								run?.rowsNeedMapping ? run.rowsNeedMapping : null,
							],
							["rows", "Rows", run?.rowsTotal ?? null],
						] as const
					).map(([key, label, count]) => (
						<Button
							key={key}
							onClick={() => setSurface(key)}
							size="sm"
							variant={surface === key ? "default" : "outline"}
						>
							{label}
							{count === null ? null : (
								<span className="ml-2 text-xs opacity-70">{count}</span>
							)}
						</Button>
					))}
				</div>
			) : null}

			{surface === "columns" && header.length > 0 ? (
				<Card>
					<CardHeader>
						<CardTitle>Columns</CardTitle>
						<CardDescription>
							Map each column in the file to a field. Unmapped columns are kept
							with the row so a reviewer can still read them.
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-2 md:grid-cols-2">
						{header.map((column) => (
							<div
								key={column}
								className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-2"
							>
								<span className="truncate text-sm font-medium">{column}</span>
								<Select
									value={effectiveMapping[column] || IGNORE}
									onValueChange={(value) =>
										setMapping({
											...effectiveMapping,
											[column]: value === IGNORE ? "" : value,
										})
									}
								>
									<SelectTrigger aria-label={`Map ${column}`}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{targetsFor(column).map((target) => (
												<SelectItem
													key={target.value || IGNORE}
													value={target.value || IGNORE}
												>
													{target.label}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
						))}
					</CardContent>
				</Card>
			) : null}

			{surface === "categories" && mappingCard && run?.sourceId ? (
				<Card>
					<CardHeader>
						<CardTitle>What this source's category words mean</CardTitle>
						<CardDescription>
							One decision per phrase, not per row, and it is remembered for
							every later file from this source. Saving re-runs the dry run, so
							the counts above follow it. A phrase can still hold rows back
							after it is mapped, when those rows are waiting on something else.
						</CardDescription>
					</CardHeader>
					<CardContent>
						{/*
						 * THE CARD OWNS ITS OWN FETCH STATE. Handing the editor an empty
						 * phrase list while this query is still in flight would have it say
						 * "Nothing is waiting on a category" on a run that is holding rows
						 * back right now -- the card is mounted only when it is.
						 */}
						{mappingCard.state === "loading" ? (
							// `role="status"`, not bare skeletons: two grey bars announce nothing,
							// and a row's "Map this phrase" button can already be clicked while this
							// is in flight -- the operator is told what it is waiting for.
							<div className="grid gap-2" role="status">
								<span className="sr-only">
									Loading what this source's category words mean.
								</span>
								<Skeleton className="h-9 w-full" />
								<Skeleton className="h-9 w-full" />
							</div>
						) : mappingCard.state === "failed" ? (
							<p className="text-sm text-destructive" role="alert">
								{apiErrorMessage(
									phrases.error,
									"Could not load the phrases this run is waiting on.",
								)}
							</p>
						) : (
							<CategoryMappingEditor
								disabledReason={
									run.status === "committed" ||
									run.status === "reverted" ||
									run.status === "discarded"
										? "This run is finished, so its rows cannot be re-planned. Upload the file again to plan a new run."
										: null
								}
								onMapped={() => resolve.mutate(undefined)}
								// Every held-back phrase, INCLUDING the no-phrase bucket -- the
								// editor drops that one itself and reports it through
								// `unmappableRows`. Never `undefined`, which is how the editor is
								// told there is no run at all; this branch has a settled one.
								phrases={mappingCard.phrases}
								sourceId={run.sourceId}
								unmappableRows={mappingCard.unmappableRows}
							/>
						)}
					</CardContent>
				</Card>
			) : null}

			{surface === "rows" ? (
				<Card>
					<CardHeader>
						<CardTitle>Rows</CardTitle>
						<CardDescription>
							Grouped by what committing would do to each one.
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-3">
						<div className="flex flex-wrap gap-2">
							{GROUPS.map((option) => (
								<Button
									key={option.key}
									size="sm"
									variant={option.key === group ? "default" : "outline"}
									onClick={() => setGroup(option.key)}
								>
									{option.label}
								</Button>
							))}
						</div>

						{rows.length === 0 ? (
							<EmptyState
								title="Nothing in this group"
								hint={
									run.status === "staged"
										? "Run the dry run to see what this file would do."
										: "Try another group."
								}
							/>
						) : (
							<ul className="grid gap-3">
								{rows.map((row) => (
									<li
										key={row.id}
										className="grid gap-2 rounded-2xl border p-3"
									>
										<div className="flex flex-wrap items-center justify-between gap-2">
											<span className="text-sm font-medium">
												Line {row.lineNo}
											</span>
											<div className="flex items-center gap-2">
												{row.action ? (
													// A plain Badge, not `StatusBadge`: that component maps
													// STORED status values to colours, and an action is a
													// different vocabulary. Passing a human label through it
													// would render every action in the neutral tone anyway.
													<Badge variant="default">
														{ACTION_LABELS[row.action]}
													</Badge>
												) : null}
												<StatusBadge status={row.matchStatus} />
												{row.reviewedAt ? (
													// A decision somebody made, distinguished from a row
													// nobody has looked at. Both reach a commit as "not
													// applied" when skipped, and a run screen that
													// reported a backlog as decided would be lying.
													<span className="text-xs text-muted-foreground">
														reviewed
													</span>
												) : null}
											</div>
										</div>

										<pre className="overflow-x-auto rounded-xl bg-slate-50 p-2 text-xs">
											{row.rawText}
										</pre>

										{row.error ? (
											<p className="text-sm text-rose-700">{row.error}</p>
										) : null}

										{/*
										 * Offered on the one hold a mapping clears, in the one group
										 * that hold appears in, and only once the phrase list it aims
										 * at has arrived.
										 *
										 * NOT "only when its picker is on screen" -- that is more than
										 * this knows, which is why the click still checks and the
										 * toast still has something to say.
										 *
										 * All three are load-bearing. `heldPhraseOf` reads the row's
										 * own reason because phrase presence does not distinguish the
										 * three holds. The group matters because a MATCHED row carries
										 * the same reason while being planned anyway -- it appears
										 * under Changed or Price-only, where mapping its phrase
										 * changes nothing. And `rowPhraseIsListed` withholds the
										 * button while the phrase list is still loading or failed.
										 */}
										{selected.hold === "needs_mapping" &&
										rowPhraseIsListed(row) ? (
											<Button
												onClick={() => {
													// The picker lives on the Categories tab, which is
													// not rendered while this row is on screen -- so
													// this asks for the tab and the effect above does
													// the focusing once it exists. Focusing here would
													// find nothing, every time.
													setPendingPhrase(heldPhraseOf(row) as string);
													setSurface("categories");
												}}
												size="sm"
												type="button"
												variant="outline"
											>
												Map this phrase
											</Button>
										) : null}

										{row.matchStatus === "ambiguous" ? (
											<div className="grid gap-2">
												<p className="text-sm text-muted-foreground">
													This could be {row.candidates?.length ?? 0} existing
													product
													{(row.candidates?.length ?? 0) === 1 ? "" : "s"}.
												</p>
												<ul className="grid gap-2">
													{(row.candidates ?? []).map((candidate) => (
														<li
															key={candidate.productId}
															className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-2"
														>
															<span className="text-sm">
																{candidate.name}
																{candidate.brandSku ? (
																	<span className="text-muted-foreground">
																		{" "}
																		· {candidate.brandSku}
																	</span>
																) : null}
															</span>
															<Button
																size="sm"
																variant="outline"
																disabled={review.isPending}
																onClick={() =>
																	review.mutate({
																		rowId: row.id,
																		decision: {
																			kind: "same_product",
																			productId: candidate.productId,
																		},
																	})
																}
															>
																This one
															</Button>
														</li>
													))}
												</ul>
												<div className="flex gap-2">
													<Button
														size="sm"
														variant="outline"
														disabled={review.isPending}
														onClick={() =>
															review.mutate({
																rowId: row.id,
																decision: { kind: "new_product" },
															})
														}
													>
														New product
													</Button>
													<Button
														size="sm"
														variant="ghost"
														disabled={review.isPending}
														onClick={() =>
															review.mutate({
																rowId: row.id,
																decision: { kind: "skip" },
															})
														}
													>
														Skip
													</Button>
												</div>
											</div>
										) : (
											<DiffTable row={row} />
										)}
									</li>
								))}
							</ul>
						)}
					</CardContent>
				</Card>
			) : null}

			<Dialog
				open={confirming === "commit"}
				titleId={commitTitleId}
				onClose={() => setConfirming(null)}
			>
				<h2 id={commitTitleId} className="text-lg font-semibold">
					Commit this run?
				</h2>
				<p className="text-sm text-muted-foreground">
					{frozen?.rowsNew ?? 0} products will be created and{" "}
					{frozen?.rowsChanged ?? 0} updated.
				</p>
				{/*
				 * WITHHELD SEPARATELY, because they are withheld for different reasons
				 * and a single total hides which: one waits on a category, the other was
				 * refused by validation.
				 *
				 * NEITHER is beyond recovery, which an earlier version of this copy
				 * asserted about the errored rows. `validateImport` recomputes
				 * `match_status` for every row nobody has reviewed, and several of its
				 * errors ARE mapping failures -- one says "Map the column that carries
				 * the brand's own code" in as many words. Telling an operator a remedy
				 * will not work when it will is the same defect as naming one that
				 * cannot.
				 */}
				<ul className="grid gap-1 text-sm text-muted-foreground">
					<li>
						{frozen?.rowsUnmatched ?? 0} rows still need a decision and will not
						be applied.
					</li>
					<li>
						{frozen?.rowsNeedMapping ?? 0} rows are held back waiting on a
						category.
					</li>
					<li>
						{frozen?.rowsErrored ?? 0} rows were refused by the last dry run.
						Some of those reasons are mapping mistakes -- an unmapped brand SKU
						column is one -- so correcting the mapping and running the dry run
						again can recover them.
					</li>
				</ul>
				<p className="text-sm text-muted-foreground">
					Every new product lands as a draft. Nothing becomes visible to
					customers until somebody publishes it.
				</p>
				{/*
				 * THE ONE THING THIS DIALOG ASKS FOR RATHER THAN REPORTS. Ops both
				 * register a source and commit its files, so nothing else catches a
				 * misregistered basis before thousands of products inherit it.
				 *
				 * A native checkbox with a visible `<label>`: the admin has no checkbox
				 * primitive, and `category-picker.tsx` records a real bug from shipping
				 * a control with no accessible name.
				 *
				 * FIRST FOCUSABLE CONTROL IN THE DIALOG, and that is what puts focus
				 * here: `Dialog` focuses `focusable()[0]`. So the order of these
				 * children IS the focus behaviour -- moving this below the button row
				 * would hand focus to Cancel. No `autoFocus`, which would be redundant
				 * with the shell; and the plan's worry about borrowing
				 * `bulk-confirm-dialog.tsx`'s focus-Confirm rule does not arise,
				 * because the shell never focuses Confirm.
				 */}
				<label className="flex items-start gap-2 text-sm" htmlFor={basisAckId}>
					<input
						checked={basisAcknowledged}
						id={basisAckId}
						onChange={(event) => setBasisAcknowledged(event.target.checked)}
						type="checkbox"
					/>
					<span>{basisSentence(frozen?.basis ?? null)}</span>
				</label>

				{/*
				 * The REASON Commit is off, carrying the id `aria-describedby` points
				 * at. Separate from the correction advice below: "change it on the
				 * source" explains what to do about a WRONG basis, which is not why the
				 * button is disabled.
				 */}
				{basisAcknowledged ? null : (
					<p className="text-sm text-amber-900" id={basisAckReasonId}>
						Tick the box above to confirm the basis before committing.
					</p>
				)}
				{/*
				 * WHERE A WRONG BASIS IS CORRECTED. The case this gate exists to catch
				 * is an operator who reads the sentence above and sees it does not match
				 * the document -- and this dialog cannot change the basis, which lives on
				 * the source. Asking somebody to confirm something they cannot correct is
				 * the remedy-the-screen-cannot-perform class.
				 */}
				<p className="text-sm text-muted-foreground">
					If that is wrong, change the basis on{" "}
					<Link className="underline" to="/catalog/sources">
						the source
					</Link>{" "}
					and upload the file again. Committing records it as it stands.
				</p>
				<div className="flex justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setConfirming(null)}
					>
						Cancel
					</Button>
					<Button
						size="sm"
						/*
						 * Disabled WITH its reason, associated by `aria-describedby` -- KTD4
						 * resolved the two prior plans' disagreement in favour of
						 * disable-and-explain over hide.
						 *
						 * Only for the one disabled state a person has to act on. While the
						 * request is in flight the button is disabled too and carries no
						 * description, because its visible text is already "Committing…".
						 *
						 * An `aria-label` was added there and removed: it replaced that
						 * visible text with different words, which is the Label-in-Name
						 * mismatch WCAG 2.5.3 forbids, and it said nothing the label did not.
						 */
						aria-describedby={basisAcknowledged ? undefined : basisAckReasonId}
						disabled={commit.isPending || !basisAcknowledged}
						onClick={() => {
							setActionError(null);
							commit.mutate();
						}}
					>
						{/* Distinct from the "Commit" button behind the dialog. */}
						{commit.isPending ? "Committing…" : "Commit the run"}
					</Button>
				</div>
			</Dialog>

			<Dialog
				open={confirming === "revert"}
				titleId={revertTitleId}
				onClose={() => setConfirming(null)}
			>
				<h2 id={revertTitleId} className="text-lg font-semibold">
					Revert this run?
				</h2>
				<p className="text-sm text-muted-foreground">
					Products this run created will be deleted, and prices it changed will
					be put back by recording the previous amount again.
				</p>
				<p className="text-sm text-muted-foreground">
					Anything edited, published or quoted since the import is left alone
					and reported.
				</p>
				<div className="flex justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setConfirming(null)}
					>
						Cancel
					</Button>
					<Button
						size="sm"
						variant="destructive"
						disabled={revert.isPending}
						onClick={() => {
							setActionError(null);
							revert.mutate();
						}}
					>
						{/*
						 * "Revert the run", not "Revert". The button that OPENED this
						 * dialog is still on the page behind it and says "Revert", and
						 * two controls with one name is how somebody confirms the wrong
						 * thing -- and how an accessibility tree stops being able to
						 * name either.
						 */}
						{revert.isPending ? "Reverting…" : "Revert the run"}
					</Button>
				</div>
			</Dialog>

			<Dialog
				open={confirming === "discard"}
				titleId={discardTitleId}
				onClose={() => setConfirming(null)}
			>
				<h2 id={discardTitleId} className="text-lg font-semibold">
					Discard this run?
				</h2>
				<p className="text-sm text-muted-foreground">
					Nothing in the catalogue changes, and the run and its file are kept.
					It stops waiting to be committed, and it can no longer be resolved or
					committed. To import this list later, upload the file again.
				</p>
				<div className="flex justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setConfirming(null)}
					>
						Cancel
					</Button>
					<Button
						size="sm"
						variant="destructive"
						disabled={discard.isPending}
						onClick={() => {
							setActionError(null);
							discard.mutate();
						}}
					>
						{discard.isPending ? "Discarding…" : "Discard the run"}
					</Button>
				</div>
			</Dialog>
		</div>
	);
}
